import { Elements } from '@stripe/react-stripe-js';
import { Appearance, loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import React, { useMemo } from 'react';

import { isStripeConfigured, stripeConfig } from './stripeConfig';

/**
 * Matched to the shop's checkout appearance rather than invented here, so the Stripe-rendered payment
 * and address fields look the same in both places and only one set of tokens has to be maintained.
 */
const APPEARANCE: Appearance = {
  theme: 'flat',
  variables: {
    fontFamily:
      "'MessinaSans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSizeBase: '16px',
    fontWeightNormal: '400',
    fontWeightMedium: '500',
    fontWeightBold: '600',
    borderRadius: '8px',
    colorPrimary: '#000000',
    colorText: '#000000',
    colorTextSecondary: '#737373',
    colorDanger: '#ff004f',
    spacingGridRow: '12px',
  },
  rules: {
    '.Label': {
      fontWeight: '500',
      fontSize: '14px',
      marginBottom: '6px',
    },
  },
};

/**
 * Wraps children in Stripe's Elements provider, always in **setup mode**.
 *
 * Setup mode is the only mode nano needs. The shopper has just registered a chip, so they are
 * always authenticated and always have a Recurly account by the time they reach checkout;
 * `stripeCheckoutEndpoint` stores the confirmation token as billing info before the purchase
 * mutation runs. Payment mode exists for accounts Recurly has never seen, which cannot happen here.
 *
 * `setupFutureUsage: 'off_session'` is required, not stylistic: Recurly's server-side intent
 * hardcodes it and rejects confirmation tokens that disagree.
 *
 * Deliberately absent: `paymentMethodTypes` (conflicts with Recurly's `automatic_payment_methods`,
 * so the method list is controlled from the Stripe dashboard's payment method configuration) and
 * `captureMethod`.
 *
 * Renders children unwrapped when Stripe is not configured so local dev still boots without keys.
 */
export default function StripeElementsProvider({ children }: { children: React.ReactNode }) {
  const stripePromise = useMemo(() => {
    if (!isStripeConfigured()) {
      console.error(
        '[stripe] no publishable key or account id — Elements will not mount. check VITE_STRIPE_* in the deployed stack',
      );
      return null;
    }
    return loadStripe(stripeConfig.publishableKey, { stripeAccount: stripeConfig.accountId });
  }, []);

  const options: StripeElementsOptions = useMemo(() => {
    return {
      mode: 'setup',
      currency: 'usd',
      loader: 'auto',
      // required for createConfirmationToken(); without it the element creates a PaymentMethod
      paymentMethodCreation: 'manual',
      setupFutureUsage: 'off_session',
      appearance: APPEARANCE,
      // where redirect-based methods (wallets, and anything the dashboard config adds later) come
      // back to. the checkout screen owns resuming from a redirect.
      return_url: `${window.location.origin}/register/checkout`,
    };
  }, []);

  if (!stripePromise) {
    return <>{children}</>;
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
}
